import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import autobind from 'class-autobind';

/*

  Given a `src` prop render an <img /> inside this component (ignoring
  children) with styling set to "fit" the image inside the container.

  Implicitly, this element should therefore have some fixed size for
  this to make sense. 

  If no src is given, render the components children as normal

  TODO: Allow this container to be resized by recalculating the fit
  on resize

  Historical note: this was created from the `loadFittedImg` jquery
  spaghetti function, which is why its a bit crazy

*/
class FittedImageContainer extends Component {

  constructor(props) {
    super(props);
    this.imgContainer = null;

    this.state = {
      isLoading: !!props.src,
      isLoadingFailed: null,
      imgCss: {
        'visibility': 'hidden',
        'position': 'absolute'
      },
      containerCss: {

      }
    };
    autobind(this);
  }

  handleImgLoad(img) {
    const $container = $(this.imgContainer);
    const enableBackgroundCover = this.props.enableBackgroundCover;

    var width, height;
    var trueWidth = img.naturalWidth || img.width; 
    var trueHeight = img.naturalHeight || img.height;
    
    // in IE 11, on first uncached load of an image,
    // sometimes it will mistake the image for the "broken image" default image
    // which has a height of 30 and a width of 28. So we just blacklist
    // that size in a heinous hack, whose goal is to make IE11 actually work
    if(!trueHeight || !trueWidth || (trueHeight == 30 && trueWidth == 28)){
      return;
    }
    
    const hideBackgroundCover = ()=> {
      this.setState(({ imgCss, containerCss, ...state })=> ({
        imgCss: {
          ...imgCss,
          display: 'inline-block'
        },
        containerCss: {
          ...containerCss,
          'backgroundImage': ''
        },
        ...state
      })); 
    };

    var imageRatio = (trueWidth * 1.0) / trueHeight;
    var containerRatio = ($container.width() * 1.0) / $container.height();
    if (imageRatio > containerRatio){
      width = Math.min($container.width(), trueWidth);
      height = width / imageRatio;
    } else{
      height = Math.min($container.height(), trueHeight);
      width = height * imageRatio;
    }
    
    this.setState(({ imgCss, containerCss })=> ({
      imgCss: {
        ...imgCss,
        visibility: 'visible'
      },
      containerCss,
      isLoading: false,
      isLoadingFailed: false
    }));


    if (trueWidth < $container.width() && trueHeight < $container.height()) {

      if (enableBackgroundCover) {
        hideBackgroundCover();
      }   

      this.setState(({ imgCss, ...state })=> ({
        imgCss: {
          ...imgCss,
          'left': '50%',
          'marginLeft': '-'+ (trueWidth/2) + 'px',
          'top': '50%',
          'marginTop': '-'+ (trueHeight/2) + 'px',
          'height': '' + trueHeight + 'px',
          'width': '' + trueWidth + 'px'
        },
        ...state
      }));

    } else if (enableBackgroundCover && trueWidth > $container.width() && trueHeight > $container.height()) {

      this.setState(({ imgCss, containerCss, ...state })=> ({
        imgCss: {
          ...imgCss,
          display: 'none'
        },
        containerCss: {
          ...containerCss,
          'backgroundImage': `url(${this.props.src})`,
          'backgroundSize': 'cover',
          'backgroundPosition': 'center',
          'backgroundRepeat': 'no-repeat'
        },
        ...state
      }));

    } else if (imageRatio > containerRatio){

      if (enableBackgroundCover) {
        hideBackgroundCover();
      }      

      this.setState(({ imgCss, ...state })=> ({
        imgCss: {
          ...imgCss,
          'width': '' + (width - 2) + 'px', //prevent auto scroll
          'height': 'auto',
          'top': '50%',
          'marginTop': '-'+ (height/2) + 'px',
          'left': '0',
          'marginLeft': '0px'
        },
        ...state
      }));

    } else {

      if (enableBackgroundCover) {
        hideBackgroundCover();
      }

      this.setState(({ imgCss, ...state })=> ({
        imgCss: {
          ...imgCss,
          'width': 'auto',
          'height':  '' + (height - 2) + 'px', //prevent auto scroll
          'left': '50%',
          'marginLeft': '-'+ (width/2) + 'px',
          'top': '0',
          'marginTop': '0px'
        },
        ...state
      }));      
    }
  }

  render() {
    const { containerCss, imgCss, isLoading, isLoadingFailed } = this.state;
    const { 
      children, 
      loadingIcon, 
      loadingFailedIcon, 
      src, 
      style, 
      ...props 
    } = omit(this.props, 'enableBackgroundCover');
    return (
      <div
        ref={el => this.imgContainer = el}
        style={{
          ...containerCss,
          ...style
        }}
        {...props}
      >
        { src ? 

          <Fragment>

            { isLoading && loadingIcon }
            { isLoadingFailed && loadingFailedIcon }
            <img 
              src={src}
              style={imgCss}
              onLoad={(e)=> {
                this.handleImgLoad(e.target);
              }}
              onError={()=> { this.setState({ 
                isLoading: false,
                isLoadingFailed: true 
              }); }}
            /> 

          </Fragment>

        : children 

      }
      </div>
    );
  }
}

FittedImageContainer.propTypes = {
  enableBackgroundCover: PropTypes.bool,
  src: PropTypes.string,
  // the Fitted Image system is used in many contexts so we allow parents to BYO-loading icon
  loadingIcon: PropTypes.node,
  loadingFailedIcon: PropTypes.node
};

FittedImageContainer.defaultProps = {
  enableBackgroundCover: true
};


export const FittedImgSafe = (props)=>
  props.src ? <FittedImageContainer {...props} /> : null;

export default FittedImageContainer;
